import { useCallback, useState } from 'react';

/**
 * Story 24.3 — the open state a layer root owns (Sheet, Dialog, AlertDialog,
 * FilterSheet), the 24.2b `DropdownMenu` pattern in one place.
 *
 * - Controlled when `open` is defined: the value is the caller's, and
 *   `setOpen` only reports through `onOpenChange`.
 * - Uncontrolled otherwise: starts at `defaultOpen` and keeps its own state,
 *   still reporting every change through `onOpenChange`.
 *
 * The root needs the value itself (not just Radix's internal state) so it
 * can hand it to `useBackDismiss`.
 */
export function useControllableOpen(
  open: boolean | undefined,
  defaultOpen: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
): [boolean, (next: boolean) => void] {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
  const controlled = open !== undefined;
  const value = controlled ? open : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  return [value, setOpen];
}
